import React, { useRef, useEffect, useState } from 'react';

import RouterLink from '../../Router/Link';

import {
  PromotionSliderWrapper,
  Slider,
  SliderItem,
  NavContainer,
  NavLeft,
  NavRight,
  Headline,
  Subline,
  Subtail,
} from './wrappers';

import Distance from '../Distance';
import { Picture } from '../Image';
import { ImageWrapper } from '../Image/wrappers';

import { ChevronLeftIcon, ChevronRightIcon } from '../Icon';

interface IPromotionSlider {
  promotions: {
    picture: IResponsivePicture;
    headline: string;
    subline: string;
    subtail: string;
    link: string;
  }[];
  desktopColumns: number;
}

const stepSize = 1;
let persistedScrollPosition = 0;

interface ISliderItemSelector {
  children: React.ReactNode;
  link?: string;
  desktopColumns: number;
}

const SliderItemSelector = ({ children, link, desktopColumns }: ISliderItemSelector) => {
  if (link) {
    return (
      <RouterLink Component={SliderItem} href={link} desktopColumns={desktopColumns}>
        {children}
      </RouterLink>
    );
  }
  return (
    <SliderItem as="span" desktopColumns={desktopColumns}>
      {children}
    </SliderItem>
  );
};

const PromotionSlider = ({ promotions, desktopColumns }: IPromotionSlider) => {
  const [showNavLeft, setShowNavLeft] = useState(false);
  const [showNavRight, setShowNavRight] = useState(false);
  const sliderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.scrollLeft = persistedScrollPosition;
    }

    checkNavState();

    sliderRef.current?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkResize);

    return () => {
      sliderRef.current?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkResize);
    };
  }, [sliderRef]);

  let scrollDebounce = 0;
  let resizeDebounce = 0;

  const handleDebounce = () => {
    checkNavState();

    if (sliderRef.current) {
      persistedScrollPosition = sliderRef.current.scrollLeft;
    }
  };

  const checkScroll = () => {
    clearTimeout(scrollDebounce);
    scrollDebounce = window.setTimeout(handleDebounce, 16);
  };

  const checkResize = () => {
    clearTimeout(resizeDebounce);
    resizeDebounce = window.setTimeout(handleDebounce, 100);
  };

  const checkNavState = () => {
    setShowNavLeft(!!sliderRef.current?.scrollLeft);
    setShowNavRight(
      (sliderRef.current?.scrollLeft || 0) <
        (sliderRef.current?.scrollWidth || 0) - (sliderRef.current?.offsetWidth || 0),
    );
  };

  const onNavLeft = () => {
    sliderRef.current?.scrollTo({
      left: (sliderRef.current?.scrollLeft || 0) - sliderRef.current?.offsetWidth * stepSize,
      behavior: 'smooth',
    });
  };

  const onNavRight = () => {
    sliderRef.current?.scrollTo({
      left: (sliderRef.current?.scrollLeft || 0) + sliderRef.current?.offsetWidth * stepSize,
      behavior: 'smooth',
    });
  };

  return (
    <PromotionSliderWrapper>
      <Slider ref={sliderRef}>
        {promotions.map(({ picture, headline, link, subline, subtail }) => (
          <SliderItemSelector
            link={link}
            key={picture.image.src + headline}
            desktopColumns={desktopColumns}>
            <ImageWrapper className="fill" ratio={1}>
              <Picture {...picture} />
            </ImageWrapper>
            <Distance />
            {headline && <Headline>{headline}</Headline>}
            {(subline || subtail) && <Distance size="half" />}
            {subline && <Subline>{subline}</Subline>}
            {subtail && <Subtail>{subtail}</Subtail>}
          </SliderItemSelector>
        ))}
      </Slider>
      {promotions.length > desktopColumns && (
        <NavContainer
          style={{
            paddingBottom: `calc(${100 / desktopColumns}% - 12px)`,
          }}>
          {showNavLeft && (
            <>
              <NavLeft onClick={onNavLeft}>
                <ChevronLeftIcon strokeWidth="2" />
              </NavLeft>
            </>
          )}
          {showNavRight && (
            <>
              <NavRight onClick={onNavRight}>
                <ChevronRightIcon strokeWidth="2" />
              </NavRight>
            </>
          )}
        </NavContainer>
      )}
    </PromotionSliderWrapper>
  );
};

export default PromotionSlider;
